import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Alert,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Collapse
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import {
  CreditCard as CreditCardIcon,
  Lock as LockIcon,
  Security as SecurityIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import PayPalCheckout from './PayPalCheckout';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  productId: string;
}

interface CheckoutProps {
  items: CartItem[];
  onBackToCart: () => void;
  onPaymentSuccess: (details: any) => void;
  onPaymentError: (error: any) => void;
  onPaymentCancel: () => void;
  onNavigateAway?: () => void;
}

type CheckoutStep = 'review' | 'payment' | 'confirmation';

const Checkout: React.FC<CheckoutProps> = ({
  items,
  onBackToCart,
  onPaymentSuccess,
  onPaymentError,
  onPaymentCancel,
  onNavigateAway
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('review');
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [error, setError] = useState<any>(null);
  const [downloadLinks, setDownloadLinks] = useState<any[]>([]);
  const [hasNavigated, setHasNavigated] = useState(false);

  const totalAmount = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const tax = totalAmount * 0.08; // 8% tax
  const finalTotal = totalAmount + tax;

  // Prevent automatic navigation from success page
  useEffect(() => {
    if (currentStep === 'confirmation' && paymentStatus === 'success') {
      // Show confirmation dialog when user tries to leave
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (!hasNavigated) {
          const message = 'Your order was successful! Are you sure you want to leave this page?';
          e.preventDefault();
          e.returnValue = message;
          // Set a flag to clear cart when user returns
          sessionStorage.setItem('clearCartOnReturn', 'true');
          return message;
        }
      };

      // Handle browser back/forward buttons
      const handlePopState = (e: PopStateEvent) => {
        if (!hasNavigated) {
          e.preventDefault();
          const confirmLeave = window.confirm('Your order was successful! Are you sure you want to leave this page?');
          if (confirmLeave) {
            setHasNavigated(true);
            onNavigateAway?.();
            window.history.go(1); // Allow the navigation
          } else {
            window.history.pushState(null, '', window.location.href); // Stay on current page
          }
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      
      // Push current state to prevent back button from going to previous step
      window.history.pushState(null, '', window.location.href);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [currentStep, paymentStatus, hasNavigated]);

  const handleProceedToPayment = () => {
    setCurrentStep('payment');
  };

  const handleBackToReview = () => {
    setCurrentStep('review');
  };

  const handlePaymentSuccess = (details: any) => {
    setPaymentStatus('success');
    setCurrentStep('confirmation');
    
    // Store download links if available
    if (details.downloads && details.downloads.length > 0) {
      setDownloadLinks(details.downloads);
    } else {
      // If no downloads in response, try to fetch them
      fetchUserDownloads();
    }
    
    onPaymentSuccess(details);
  };

  // Add function to fetch user downloads
  const fetchUserDownloads = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/downloads/user`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDownloadLinks(data.downloads || []);
      }
    } catch (error) {
      console.error('Error fetching downloads:', error);
    }
  };

  const handlePaymentError = (error: any) => {
    console.error('Payment error in Checkout:', error);
    setPaymentStatus('error');
    
    // Provide more specific error messages
    let errorMessage = 'Payment failed. Please try again.';
    
    if (error?.message) {
      if (error.message.includes('INSTRUMENT_DECLINED')) {
        errorMessage = 'Payment was declined. Please try a different payment method.';
      } else if (error.message.includes('PAYER_ACTION_REQUIRED')) {
        errorMessage = 'Additional action required. Please complete the payment process.';
      } else if (error.message.includes('PAYMENT_SOURCE_INFO_CANNOT_BE_VERIFIED')) {
        errorMessage = 'Payment information could not be verified. Please check your details.';
      } else if (error.message.includes('PAYMENT_SOURCE_DECLINED_BY_PROCESSOR')) {
        errorMessage = 'Payment was declined by the processor. Please try again.';
      } else if (error.message.includes('PAYMENT_SOURCE_NOT_SUPPORTED')) {
        errorMessage = 'This payment method is not supported. Please use PayPal.';
      } else {
        errorMessage = `Payment error: ${error.message}`;
      }
    }
    
    const errorWithMessage = { ...error, userMessage: errorMessage };
    setError(errorWithMessage);
    onPaymentError(errorWithMessage);
  };

  const handlePaymentCancel = () => {
    onPaymentCancel();
  };

  const handleDownloadFromUrl = async (downloadUrl: string, fileName: string) => {
    try {
      if (!downloadUrl || typeof downloadUrl !== 'string') {
        console.error('Invalid download URL:', downloadUrl);
        return;
      }
      
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const renderReviewStep = () => (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Review Your Order
      </Typography>

      {/* Order Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Order Summary
          </Typography>
          
          <List>
            {items.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar 
                      src={item.image} 
                      variant="rounded"
                      sx={{ width: 50, height: 50 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.name}
                    secondary={`Quantity: ${item.quantity}`}
                  />
                  <Typography variant="body1" fontWeight={600}>
                    ${(item.price * item.quantity).toFixed(2)}
                  </Typography>
                </ListItem>
                {index < items.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Totals */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography>Subtotal:</Typography>
              <Typography>${totalAmount.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography>Tax (8%):</Typography>
              <Typography>${tax.toFixed(2)}</Typography>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" fontWeight={600}>Total:</Typography>
              <Typography variant="h6" fontWeight={600}>${finalTotal.toFixed(2)}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Payment Method Preview */}
      <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CreditCardIcon sx={{ color: '#0070ba', fontSize: 32 }} />
            <Box>
              <Typography variant="h6">PayPal</Typography>
              <Typography variant="body2" color="text.secondary">
                Pay securely with your PayPal account
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Security Notice */}
      <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">Secure Checkout</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Your payment information is encrypted and secure. We use industry-standard SSL encryption to protect your data.
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onBackToCart}
          startIcon={<ArrowBackIcon />}
        >
          Back to Cart
        </Button>
        <Button
          variant="contained"
          onClick={handleProceedToPayment}
          size="large"
        >
          Proceed to Payment
        </Button>
      </Box>
    </Box>
  );

  const renderPaymentStep = () => (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        PayPal Payment
      </Typography>

      {/* Order Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Order Total</Typography>
            <Typography variant="h5" fontWeight={600}>${finalTotal.toFixed(2)}</Typography>
          </Box>
          
          <Button
            variant="outlined"
            onClick={() => setShowPaymentDetails(!showPaymentDetails)}
            endIcon={showPaymentDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mb: 2 }}
          >
            View Details
          </Button>

          <Collapse in={showPaymentDetails}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Subtotal:</Typography>
                <Typography>${totalAmount.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Tax (8%):</Typography>
                <Typography>${tax.toFixed(2)}</Typography>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* PayPal Checkout */}
      <PayPalCheckout
        orderItems={items.map(item => ({
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          productId: item.productId
        }))}
        totalAmount={finalTotal}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
        onPaymentCancel={handlePaymentCancel}
      />

      <Button
        variant="outlined"
        onClick={handleBackToReview}
        sx={{ mt: 2 }}
        startIcon={<ArrowBackIcon />}
      >
        Back to Review
      </Button>
    </Box>
  );

  const renderConfirmationStep = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'success.main' }}>
          <LockIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          Payment Successful!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Thank you for your purchase. Your downloads are ready!
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Order Summary
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>Total Paid:</Typography>
            <Typography fontWeight={600}>${finalTotal.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>Payment Method:</Typography>
            <Chip 
              label="PayPal"
              color="primary"
              size="small"
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography>Items:</Typography>
            <Typography>{items.length} items</Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Download Links Section */}
      {downloadLinks.length > 0 ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
              🎉 Your Downloads Are Ready!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click the buttons below to download your purchased files:
            </Typography>
            
            <List>
              {Array.isArray(downloadLinks) && downloadLinks.length > 0 ? downloadLinks.map((download, index) => {
                // Defensive programming - ensure download object is valid
                if (!download || typeof download !== 'object') {
                  console.warn('Invalid download object at index', index, download);
                  return null;
                }
                
                return (
                <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 2 }}>
                  <ListItemText
                    primary={download.productName || `Download ${index + 1}`}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Downloads remaining: {download?.remainingDownloads || download?.maxDownloads || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Expires: {(() => {
                            try {
                              return download?.expiresAt ? new Date(download.expiresAt).toLocaleDateString() : 'N/A';
                            } catch (error) {
                              console.warn('Error formatting date:', error);
                              return 'N/A';
                            }
                          })()}
                        </Typography>
                      </Box>
                    }
                  />
                  {download?.downloadUrl ? (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleDownloadFromUrl(download.downloadUrl, download?.productName || `download-${index + 1}`)}
                      startIcon={<DownloadIcon />}
                      sx={{ mt: 1, backgroundColor: 'success.main' }}
                    >
                      Download File
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled
                      sx={{ mt: 1 }}
                    >
                      Download Unavailable
                    </Button>
                  )}
                </ListItem>
                );
              }).filter(Boolean) : (
                <ListItem>
                  <ListItemText
                    primary="No downloads available"
                    secondary="Downloads will appear here once processed"
                  />
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              You will receive a confirmation email with download links shortly.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {downloadLinks.length > 0 
          ? 'You can also access your downloads from your profile page.'
          : 'You will receive a confirmation email with download links shortly.'
        }
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          💡 This success page will remain visible until you choose to navigate away.
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => {
            setHasNavigated(true);
            onNavigateAway?.();
            navigate('/profile');
          }}
        >
          View Profile
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            setHasNavigated(true);
            onNavigateAway?.();
            navigate('/');
          }}
        >
          Continue Shopping
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Progress Stepper */}
      <Stepper activeStep={currentStep === 'review' ? 0 : currentStep === 'payment' ? 1 : 2} sx={{ mb: 4 }}>
        <Step>
          <StepLabel>Review Order</StepLabel>
        </Step>
        <Step>
          <StepLabel>Payment</StepLabel>
        </Step>
        <Step>
          <StepLabel>Confirmation</StepLabel>
        </Step>
      </Stepper>

      {/* Step Content */}
      {currentStep === 'review' && renderReviewStep()}
      {currentStep === 'payment' && renderPaymentStep()}
      {currentStep === 'confirmation' && renderConfirmationStep()}

      {/* Error Alert */}
      {paymentStatus === 'error' && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error?.userMessage || 'Payment failed. Please try again.'}
        </Alert>
      )}
    </Box>
  );
};

export default Checkout; 